import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import TrueImpactLoading from '../TrueImpactLoading';
import ProgramManagerModal from './ProgramManagerModal';
import Tooltip from '@mui/material/Tooltip/Tooltip';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactIconButton from '../../design-system/components/TrueImpactIconButton';
import { ProgramManager, Report, getFullName } from './utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faInfoCircle } from '@fortawesome/pro-light-svg-icons';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from '../../design-system/settings/variables/Colors';
import { numberWithCommas } from 'shared/utils/numberUtils';
import { useGraphQLRequest } from '../../auth/hooks/useApiRequest';
import { useClient } from '../../apollo';
import { ProgramManagers, getProgramManager } from './api';
import AllocateReport from './AllocateReport';
import ReportVisibilityModal from './ReportVisibilityModal';
import { SharingType } from 'shared/account/pages/reportSharing/ReportSharingOptions';
import { useFeatureFlags } from '../../providers/FeatureFlags/useFeatureFlags';

const tooltipText =
  'While your report isn\'t tied to a specific donor, the deadlines for submitting your report are ' +
  'determined by the sponsoring donor.';

const StyledHeader = styled.header`
  display: flex;
  align-items: space-between;
  justify-content: space-between;
  margin-bottom: ${SPACERS.extraLarge};
  background-color: ${COLORS.grayLightest};
  padding: ${SPACERS.small} ${SPACERS.medium};
  .program-manager {
    display: flex;
    align-items: center;
    .program-manager-text {
      margin-right: ${SPACERS.tiny};
    }
    .edit-icon {
      margin-left: ${SPACERS.tiny};
    }
  }
  .program-info {
    display: flex;
    align-items: center;
    .icon {
      cursor: pointer;
    }
    .program-sponsor-text {
      margin-left: ${SPACERS.tiny};
      margin-right: ${SPACERS.small};
    }
  }
`;

export interface HeaderEventProps {
  report: Report;
  isThirdParty: boolean;
}

const Header: React.FC<HeaderEventProps> = ({ report, isThirdParty }) => {
  const client = useClient();
  const reportVisibility = useFeatureFlags('reportVisibility');
  const [requestState, makeRequest] = useGraphQLRequest<{
    allPrograms: ProgramManagers[];
  }>();
  const [open, setOpen] = useState<boolean>(false);
  const [visibility, setVisibility] = useState<SharingType>(
    report.visibility === 'PUBLIC' ? 'public' : 'private',
  );
  const [openVisibility, setOpenVisibility] = useState<boolean>(false);
  const [managers, setManagers] = useState<ProgramManager[]>([]);
  const [currentProgramManager, setCurrentProgramManager] = useState<
    ProgramManager | undefined
  >(report.programManager || undefined);

  useEffect(() => {
    if (client) {
      makeRequest(getProgramManager(client, report.dbId, report.programId));
    }
  }, [report]);

  useEffect(() => {
    if (requestState.type !== 'REQUEST_SUCCESS') return;
    const program = requestState.data.allPrograms[0];
    if (!program || !program.manager) return;
    setCurrentProgramManager({
      userId: program.managerId,
      accountId: program.manager.accountId,
      email: program.manager.email || undefined,
      title: program.manager.title || undefined,
      phone: program.manager.phone || undefined,
      lastName: program.manager.lastName || undefined,
      firstName: program.manager.firstName || undefined,
    });
    if (program.managers.length === 0) return;
    setManagers(
      program.managers.map(p => ({
        accountId: p.accountId,
        userId: p.id,
        email: p.email || undefined,
        firstName: p.first_name || undefined,
        lastName: p.last_name || undefined,
        phone: p.phone || undefined,
        title: p.title || undefined,
      })),
    );
  }, [requestState]);

  const handleOpen = (): void => setOpen(true);
  const handleClose = (): void => setOpen(false);
  const handleOpenVisibility = () => setOpenVisibility(true);
  const handleCloseVisibility = (value: SharingType) => {
    setVisibility(value);
    setOpenVisibility(false);
  };

  const loadingProgramManagerData =
    requestState.type === 'REQUEST_INIT' ||
    requestState.type === 'REQUEST_START';

  return (
    <StyledHeader>
      <TrueImpactText className="program-manager report-builder-program-manager">
        <strong className="program-manager-text">Program manager</strong>{' '}
        {loadingProgramManagerData ? (
          <TrueImpactLoading singleLine={true} marginTop={0} marginBottom={0} />
        ) : (
          getFullName(
            currentProgramManager?.firstName,
            currentProgramManager?.lastName,
          )
        )}
        {!loadingProgramManagerData && (
          <TrueImpactIconButton
            icon={faPen}
            backgroundColor="greyLight"
            onClick={handleOpen}
            className="edit-icon"
          />
        )}
        <ProgramManagerModal
          options={managers}
          reportId={report.dbId}
          programId={report.programId}
          currentProgramManager={currentProgramManager}
          handleCurrentManagerChange={setCurrentProgramManager}
          open={open}
          onClose={handleClose}
        />
      </TrueImpactText>
      {(!report.totalDonations || report.totalDonations === 0) && (
        <AllocateReport report={report} />
      )}
      {!isThirdParty && (
        <>
          {reportVisibility && (
            <TrueImpactText className="program-manager">
              <strong className="program-manager-text">
                Report visibility
              </strong>{' '}
              {visibility === 'public' ? 'Public' : 'Restricted'}
              <TrueImpactIconButton
                icon={faPen}
                backgroundColor="greyLight"
                onClick={handleOpenVisibility}
                className="edit-icon"
              />
              <ReportVisibilityModal
                report={report}
                value={visibility}
                open={openVisibility}
                onClose={handleCloseVisibility}
              />
            </TrueImpactText>
          )}
          <div className="program-info report-builder-program-sponsor">
            <Tooltip arrow={true} title={tooltipText}>
              <div>
                <FontAwesomeIcon icon={faInfoCircle} className="icon" />
              </div>
            </Tooltip>
            <TrueImpactText
              variant="body-bold"
              className="program-sponsor-text"
            >
              Program sponsor
            </TrueImpactText>
            <div className="program-donation-info">
              <div>{report.sponsorName || 'Unknown'}</div>
              <div>
                {report.year} Donation &mdash; $
                {numberWithCommas(report.totalDonations || 0)}
              </div>
            </div>
          </div>
        </>
      )}
    </StyledHeader>
  );
};

export default Header;
